import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_AUDIO_DURATION_MS = 1000;
const MAX_AUDIO_DURATION_SECONDS = 120;

type UseAudioRecordingOptions = {
  enabled: boolean;
  onRecordingComplete: (file: File) => void;
  onError: (message: string) => void;
};

export function useAudioRecording({
  enabled,
  onRecordingComplete,
  onError,
}: UseAudioRecordingOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number>(0);
  const discardRecordingRef = useRef(false);

  const cleanupStream = useCallback(() => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(track => track.stop());
      recordingStreamRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(
    (discard = false) => {
      discardRecordingRef.current = discard;
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        cleanupStream();
        setIsRecording(false);
      }
    },
    [cleanupStream],
  );

  const cancelRecording = useCallback(() => {
    stopRecording(true);
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    if (isRecording || !enabled) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      discardRecordingRef.current = false;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        cleanupStream();

        const elapsedMs = recordingStartedAtRef.current
          ? Date.now() - recordingStartedAtRef.current
          : 0;
        recordingStartedAtRef.current = 0;

        if (discardRecordingRef.current || recordingChunksRef.current.length === 0) {
          recordingChunksRef.current = [];
          return;
        }

        if (elapsedMs < MIN_AUDIO_DURATION_MS) {
          recordingChunksRef.current = [];
          onError('La grabación es demasiado corta (mínimo 1 segundo).');
          return;
        }

        const blob = new Blob(recordingChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        recordingChunksRef.current = [];
        const file = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type });
        onRecordingComplete(file);
      };

      recorder.start(1000);
      recordingStartedAtRef.current = Date.now();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds(prev => {
          const next = prev + 1;
          if (next >= MAX_AUDIO_DURATION_SECONDS) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
    } catch {
      onError('No se pudo acceder al micrófono');
    }
  }, [cleanupStream, enabled, isRecording, onError, onRecordingComplete, stopRecording]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
      return;
    }
    void startRecording();
  }, [isRecording, startRecording, stopRecording]);

  useEffect(() => {
    return () => {
      cleanupStream();
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
      }
    };
  }, [cleanupStream]);

  return {
    isRecording,
    recordingSeconds,
    cancelRecording,
    toggleRecording,
  };
}

export function canUseMicrophone(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export function microphoneUnavailableMessage(): string {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return 'Para grabar audio, abrí el inbox con HTTPS. En HTTP el navegador bloquea el micrófono.';
  }
  return 'Este navegador no permite grabar audio desde el micrófono.';
}
